import styled from 'styled-components';

export type RowBackground = 'transparent' | 'cream' | 'olive_soft' | 'olive' | 'gold_soft' | 'dark';

const BG_MAP: Record<RowBackground, { bg: string; color?: string; full?: boolean }> = {
  transparent: { bg: 'transparent' },
  cream:       { bg: '#f5f5f2' },
  olive_soft:  { bg: 'rgba(101, 116, 85, 0.08)' },
  olive:       { bg: '#657455', color: '#f5f5f2' },
  gold_soft:   { bg: 'rgba(184, 150, 74, 0.10)' },
  dark:        { bg: '#1e2418', color: '#f5f5f2' },
};

export const Container = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0 0 2rem;
`;

export const RowBand = styled.div<{ background?: RowBackground }>`
  width: 100%;
  background: ${(p) => BG_MAP[p.background || 'transparent'].bg};
  color: ${(p) => BG_MAP[p.background || 'transparent'].color || 'inherit'};
  padding: ${(p) => (p.background && p.background !== 'transparent' ? '3rem 0' : '0.75rem 0')};
  /* Break out of any parent max-width container so colored bands span full viewport */
  ${(p) => (p.background && p.background !== 'transparent'
    ? 'margin-left: calc(50% - 50vw); margin-right: calc(50% - 50vw);'
    : '')}

  /* Headlines inside dark/olive bands switch to light tone */
  h1, h2, h3, h4, h5, h6 {
    color: ${(p) => BG_MAP[p.background || 'transparent'].color || 'inherit'};
  }

  a {
    color: ${(p) => (BG_MAP[p.background || 'transparent'].color
      ? '#dcc78b'
      : p.theme.colors.accent)};
  }
`;

export const RowInner = styled.div<{ background?: RowBackground }>`
  ${(p) => (p.background && p.background !== 'transparent'
    ? 'max-width: 1400px; margin: 0 auto; padding: 0 1.5rem;'
    : 'max-width: 100%; margin: 0; padding: 0;')}
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const Col = styled.div<{ flex: string }>`
  flex: ${(p) => p.flex};
  min-width: 0;
`;

export const TextContent = styled.div`
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  line-height: 1.7;

  > *:first-child {
    margin-top: 0;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Abel', sans-serif;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.main};
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }
  h1 { font-size: 2.25rem; }
  h2 { font-size: 1.65rem; }
  h3 { font-size: 1.3rem; }
  p { margin: 0 0 1rem; }
  ul, ol { padding-left: 1.5rem; margin: 0 0 1rem; }
  li { margin-bottom: 0.4rem; }
  a {
    color: ${({ theme }) => theme.colors.accent};
    text-decoration: underline;
    &:hover { color: ${({ theme }) => theme.colors.main}; }
  }
  strong { font-weight: 600; }
`;

export const ImageWrapper = styled.div`
  width: 100%;
  position: relative;
`;

export const SingleImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 4px;
`;

export const Copyright = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  font-size: 0.7rem;
  color: #fff;
  background: rgba(0, 0, 0, 0.5);
  padding: 2px 6px;
  border-radius: 2px;
`;

export const SliderWrapper = styled.div`
  width: 100%;
`;

export const SliderMain = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SliderArrow = styled.button<{ left?: boolean }>`
  position: absolute;
  ${(p) => (p.left ? 'left: 8px;' : 'right: 8px;')}
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(0, 0, 0, 0.8); }
`;

export const SliderDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 0.75rem;
`;

export const SliderDot = styled.button<{ $active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: ${(p) => (p.$active ? p.theme.colors.accent : p.theme.colors.light)};
`;
